import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  FileText,
  Search,
  Trash2,
  Edit3,
  Eye,
  Send,
  CheckCircle2,
  TrendingUp,
} from 'lucide-react';
import { BRAND } from '@/constants/brand';
import { useAuth } from '@/context/AuthContext';
import { useData } from '@/context/DataContext';
import { fmt } from '@/lib/utils';
import StatCard from '@/components/ui/StatCard';
import StatusBadge from '@/components/ui/StatusBadge';
import { useConfirm } from '@/components/ui/useConfirm';

export default function Dashboard() {
  const { user } = useAuth();
  const { quotes, deleteQuote } = useData();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [confirm, ConfirmUI] = useConfirm();

  const filtered = quotes
    .filter((q) => {
      const matchSearch =
        !search ||
        q.clienteSnapshot?.empresa?.toLowerCase().includes(search.toLowerCase()) ||
        q.clienteSnapshot?.nombre?.toLowerCase().includes(search.toLowerCase()) ||
        q.proyectoNombre?.toLowerCase().includes(search.toLowerCase()) ||
        q.folio?.toLowerCase().includes(search.toLowerCase());
      const matchStatus = filterStatus === 'all' || q.estatus === filterStatus;
      return matchSearch && matchStatus;
    }).sort((a, b) => new Date(b.fechaCreacion) - new Date(a.fechaCreacion));    

  const stats = {
    total: quotes.length,
    enviadas: quotes.filter((q) => q.estatus === 'enviada').length,
    aceptadas: quotes.filter((q) => q.estatus === 'aceptada').length,
    pipeline: quotes
      .filter((q) => q.estatus === 'enviada')
      .reduce((s, q) => s + (q.total || 0), 0),
  };

  const handleDelete = async (q) => {
    const ok = await confirm(`¿Eliminar cotización ${q.folio}?`);
    if (ok) await deleteQuote(q.id);
  };

  return (
    <div className="flex-1 overflow-auto bg-neutral-950">
      {ConfirmUI}
      <div className="max-w-7xl mx-auto px-8 py-8">
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-1">Cotizaciones</h1>
            <p className="text-neutral-500 text-sm">              
            Hola, {(user?.nombre ?? user?.name)?.split(' ')[0]}
            </p>
          </div>
          <button
            onClick={() => navigate('/cotizacion/nueva')}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium hover:scale-[1.02] transition-transform"
            style={{ background: BRAND.gold, color: BRAND.black }}
          >
            <Plus className="w-4 h-4" /> Nueva Cotización
          </button>
        </div>

        <div className="grid grid-cols-4 gap-4 mb-8">
          <StatCard icon={FileText} label="Total" value={stats.total} color={BRAND.gold} />
          <StatCard icon={Send} label="Enviadas" value={stats.enviadas} color="#3b82f6" />
          <StatCard
            icon={CheckCircle2}
            label="Aceptadas"
            value={stats.aceptadas}
            color="#10b981"
          />
          <StatCard
            icon={TrendingUp}
            label="Pipeline"
            value={fmt(stats.pipeline)}
            color={BRAND.gold}
            small
          />
        </div>

        <div className="flex items-center gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 w-4 h-4 text-neutral-500" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por cliente, empresa, proyecto o folio..."
              className="w-full pl-10 pr-4 py-2.5 bg-neutral-900 border border-neutral-800 rounded-lg text-white placeholder-neutral-600 focus:outline-none focus:border-brand-gold/50 text-sm"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2.5 bg-neutral-900 border border-neutral-800 rounded-lg text-white text-sm focus:outline-none focus:border-brand-gold/50"
          >
            <option value="all">Todos</option>
            <option value="borrador">Borradores</option>
            <option value="enviada">Enviadas</option>
            <option value="aceptada">Aceptadas</option>
            <option value="rechazada">Rechazadas</option>
          </select>
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-20 bg-neutral-900/30 rounded-2xl border border-dashed border-neutral-800">
            <FileText className="w-12 h-12 text-neutral-700 mx-auto mb-4" />
            <p className="text-neutral-400 mb-2">
              {quotes.length === 0 ? 'No hay cotizaciones todavía' : 'No hay resultados'}
            </p>
            <button
              onClick={() => navigate('/cotizacion/nueva')}
              className="text-sm font-medium hover:underline"
              style={{ color: BRAND.gold }}
            >
              Crear la primera cotización →
            </button>
          </div>
        ) : (
          <div className="bg-neutral-900/30 rounded-xl border border-neutral-800 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-neutral-800 text-xs text-neutral-500 uppercase tracking-wider">
                  <th className="text-left p-4">Folio</th>
                  <th className="text-left p-4">Cliente / Proyecto</th>
                  <th className="text-left p-4">Creado por</th>
                  <th className="text-left p-4">Fecha</th>
                  <th className="text-left p-4">Total</th>
                  <th className="text-left p-4">Estatus</th>
                  <th className="text-right p-4">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((q) => (
                  <tr
                    key={q.id}
                    className="border-b border-neutral-900 hover:bg-neutral-900/30 transition-colors"
                  >
                    <td className="p-4 font-mono text-xs text-neutral-400">{q.folio}</td>
                    <td className="p-4">
                      <div className="text-white text-sm font-medium">
                        {q.clienteSnapshot?.empresa || q.clienteSnapshot?.nombre}
                      </div>
                      <div className="text-neutral-500 text-xs">{q.proyectoNombre}</div>
                    </td>
                    <td className="p-4 text-neutral-400 text-sm">                                            
                      {q.creador?.nombre?.split(' ')[0] ?? '—'}
                    </td>
                    <td className="p-4 text-neutral-400 text-sm">
                      {new Date(q.fechaCreacion).toLocaleDateString('es-MX')}
                    </td>
                    <td className="p-4 text-white text-sm font-medium">{fmt(q.total)}</td>
                    <td className="p-4">
                      <StatusBadge status={q.estatus} />
                    </td>
                    <td className="p-4">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => navigate(`/cotizacion/${q.id}`)}
                          title="Ver"
                          className="p-2 text-neutral-500 hover:text-white hover:bg-neutral-800 rounded"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => navigate(`/cotizacion/${q.id}/editar`)}
                          title="Editar"
                          className="p-2 text-neutral-500 hover:text-white hover:bg-neutral-800 rounded"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(q)}
                          title="Eliminar"
                          className="p-2 text-neutral-500 hover:text-red-400 hover:bg-red-500/10 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
